import { cn, Table } from '@heroui/react'
import { type ReactNode } from 'react'

export type TableHeadCell = { label: string; num?: boolean }

type DataTableProps = {
  /** The accessible name; the card title reads fine. */
  label: string
  head: TableHeadCell[]
  children: ReactNode
}

/** A table that scrolls inside its card rather than crushing its label column. */
const DataTable = ({ label, head, children }: DataTableProps) => (
  <Table variant="secondary">
    <Table.ScrollContainer>
      <Table.Content aria-label={label}>
        <Table.Header>
          {head.map((cell, i) => (
            <Table.Column
              key={i}
              isRowHeader={i === 0}
              className={cn(
                'text-[0.65rem] tracking-wider uppercase',
                cell.num && 'text-right'
              )}
            >
              {cell.label}
            </Table.Column>
          ))}
        </Table.Header>
        <Table.Body>{children}</Table.Body>
      </Table.Content>
    </Table.ScrollContainer>
  </Table>
)

export default DataTable

type TrProps = {
  /** Fade a row whose headline count is zero. */
  dim?: boolean
  /** Tint the row the section is about, e.g. the real users. */
  highlight?: boolean
  children: ReactNode
}

/** A body row, with the two states the sections use. */
export const Tr = ({ dim = false, highlight = false, children }: TrProps) => (
  <Table.Row className={cn(dim && 'opacity-60', highlight && 'bg-accent/5')}>
    {children}
  </Table.Row>
)

type TdProps = {
  /** Right-aligned, tabular figures. */
  num?: boolean
  muted?: boolean
  nowrap?: boolean
  mono?: boolean
  children?: ReactNode
}

/** A body cell. Alignment and tone are props so a section never styles one. */
export const Td = ({
  num = false,
  muted = false,
  nowrap = false,
  mono = false,
  children,
}: TdProps) => (
  <Table.Cell
    className={cn(
      'text-sm',
      num && 'text-right tabular-nums',
      muted && 'text-muted',
      nowrap && 'whitespace-nowrap',
      mono && 'font-mono text-xs'
    )}
  >
    {children}
  </Table.Cell>
)

/** A bar inside a table cell, on the shared scale its column declares. */
export const InlineBar = ({
  value,
  max,
  alt = false,
  hint,
}: {
  value: number
  max: number
  alt?: boolean
  hint?: string
}) => (
  <div
    className="bg-surface-secondary h-2 min-w-20 overflow-hidden rounded-sm"
    title={hint}
  >
    <div
      className={cn('h-full', alt ? 'bg-muted' : 'bg-accent')}
      style={{ width: `${max ? (value / max) * 100 : 0}%` }}
    />
  </div>
)
